import { getUniqueID } from "../../utils/numbers";
import {
  CLIP_PLACEMENT_OVERLAY,
  getClipPlaybackRate,
  VIDEO_EDITOR_DEFAULT_COMPOSITION,
  VIDEO_EDITOR_FPS,
  VIDEO_EDITOR_MIN_CLIP_DURATION,
  VIDEO_EDITOR_TRACK_ORDER,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import { createFullCanvasClipLayout } from "./clipLayout";
import { DEFAULT_CLIP_VOLUME } from "./clipProperties";

const createTrack = ({ type }) => ({
  id: getUniqueID(),
  type,
  clipIds: [],
});

export const createDefaultTracks = () => {
  const tracks = VIDEO_EDITOR_TRACK_ORDER.map((type) => createTrack({ type }));
  const byId = Object.fromEntries(tracks.map((track) => [track.id, track]));

  return {
    byId,
    order: tracks.map((track) => track.id),
  };
};

export const getTrackByType = ({ edl, type }) => {
  const order = edl?.tracks?.order || [];
  return order
    .map((trackId) => edl?.tracks?.byId?.[trackId])
    .find((track) => track?.type === type);
};

export const createEmptyEdl = () => ({
  version: 1,
  sourceAssetID: "",
  duration: 0,
  fps: VIDEO_EDITOR_FPS,
  compositionWidth: VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
  compositionHeight: VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
  tracks: createDefaultTracks(),
  clips: {
    byId: {},
  },
  playhead: 0,
  effects: [],
  transitions: [],
  cues: [],
});

export const createInitialEdlFromVideo = ({
  sourceUrl,
  sourceAssetID = "",
  duration = 0,
  compositionWidth = VIDEO_EDITOR_DEFAULT_COMPOSITION.width,
  compositionHeight = VIDEO_EDITOR_DEFAULT_COMPOSITION.height,
  sourceWidth,
  sourceHeight,
  sourceName = "",
}) => {
  const tracks = createDefaultTracks();
  const videoTrack = getTrackByType({ edl: { tracks }, type: VIDEO_EDITOR_TRACK_TYPE.VIDEO });
  const clipId = getUniqueID();
  const safeDuration = Math.max(duration || 1, VIDEO_EDITOR_MIN_CLIP_DURATION);
  const resolvedSourceWidth =
    Number(sourceWidth) > 0 ? Number(sourceWidth) : compositionWidth;
  const resolvedSourceHeight =
    Number(sourceHeight) > 0 ? Number(sourceHeight) : compositionHeight;

  if (videoTrack) {
    tracks.byId[videoTrack.id] = {
      ...videoTrack,
      clipIds: [clipId],
    };
  }

  return {
    version: 1,
    sourceAssetID,
    sourceName,
    duration: safeDuration,
    fps: VIDEO_EDITOR_FPS,
    compositionWidth,
    compositionHeight,
    tracks,
    clips: {
      byId: {
        [clipId]: {
          id: clipId,
          trackId: videoTrack?.id,
          type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
          sourceUrl,
          label: sourceName || "Video",
          inPoint: 0,
          outPoint: safeDuration,
          position: 0,
          sourceDuration: safeDuration,
          volume: DEFAULT_CLIP_VOLUME,
          layout: createFullCanvasClipLayout(),
          sourceWidth: resolvedSourceWidth,
          sourceHeight: resolvedSourceHeight,
          layerIndex: 0,
          placement: CLIP_PLACEMENT_OVERLAY,
        },
      },
    },
    playhead: 0,
    effects: [],
    transitions: [],
    cues: [],
  };
};

export const getEdlDuration = ({ edl }) => {
  const clips = Object.values(edl?.clips?.byId || {});
  if (clips.length === 0) {
    return 0;
  }
  return clips.reduce(
    (max, clip) =>
      Math.max(max, (clip?.position || 0) + getClipTimelineDuration({ clip })),
    0
  );
};

export const getClipTimelineDuration = ({ clip }) => {
  const inPoint = Number(clip?.inPoint) || 0;
  const outPoint = Number(clip?.outPoint) || 0;
  const sourceWindow = Math.max(outPoint - inPoint, 0);
  return sourceWindow / getClipPlaybackRate({ clip });
};

export const getClipEndOnTimeline = ({ clip }) =>
  (Number(clip?.position) || 0) + getClipTimelineDuration({ clip });

export const getTrackLastClipEndTime = ({ edl, type, trackId }) => {
  if (trackId) {
    const track = edl?.tracks?.byId?.[trackId];
    if (!track?.clipIds?.length) return 0;

    return track.clipIds.reduce((maxEnd, clipId) => {
      const clip = edl?.clips?.byId?.[clipId];
      if (!clip) return maxEnd;

      return Math.max(maxEnd, getClipEndOnTimeline({ clip }));
    }, 0);
  }

  return Object.values(edl?.clips?.byId || {}).reduce((maxEnd, clip) => {
    if (!clip || (type && clip.type !== type)) {
      return maxEnd;
    }

    return Math.max(maxEnd, getClipEndOnTimeline({ clip }));
  }, 0);
};
